import { and, not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import Select from 'tio-reimagined/components/select';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type Owner from '@ember/owner';

type Option = {
  label?: string;
  value?: string;
  [key: string]: string | undefined;
};

interface Signature<T extends Option> {
  Args: {
    label?: string;
    hideLabel?: boolean;
    name?: string;
    onChange?: (value: string) => void;
    optionLabel?: keyof T;
    optionValue?: keyof T;
    options: T[];
    value?: string;
    placeholder?: string;
    selectPlaceholder?: string;
  };
  Blocks: {
    options: [T];
  };
}

export default class TioSelectTextArea<T extends Option> extends Component<Signature<T>> {
  @tracked selectedKey = '';
  @tracked textAreaValue = '';

  constructor(owner: Owner, args: Signature<T>['Args']) {
    super(owner, args);
    this.textAreaValue = args.value ?? '';
  }

  get itemsForSelect(): Array<{ key: string; label: string; item: T }> {
    const opts = this.args.options ?? [];
    const { optionLabel, optionValue } = this.args;

    return opts.map((opt) => {
      const label = String(optionLabel ? opt[optionLabel] : opt.label);

      // use the `body` when non‐empty, otherwise fall back to the title
      const raw = String(optionValue ? (opt[optionValue] ?? '') : (opt.value ?? ''));

      const key = raw !== '' ? raw : label;

      return { key, label, item: opt };
    });
  }

  get textareaId(): string {
    return this.args.name || 'select-textarea-input';
  }

  onSelectionChange = (key: string | null) => {
    this.selectedKey = key || '';
    const { onChange, optionValue } = this.args;

    // find the matching original option
    const selected = key
      ? this.args.options.find((opt) => String(optionValue ? opt[optionValue] : opt.value) === key)
      : undefined;

    this.textAreaValue = selected
      ? String(optionValue ? selected[optionValue] : selected.value)
      : '';

    onChange?.(this.textAreaValue);
  };

  focusTextArea = () => {
    const textarea = document.getElementById(this.textareaId);
    textarea?.focus();
  };

  handleTextAreaInput = (event: Event) => {
    const val = (event.target as HTMLTextAreaElement).value;
    this.textAreaValue = val;
    this.args.onChange?.(val);
  };

  <template>
    {{#if (and @label (not @hideLabel))}}
      <label for={{@name}} class="form-field-label not-sr-only">{{@label}}</label>
    {{/if}}

    <div
      class="overflow-hidden rounded-lg border border-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600"
    >
      <div class="px-1.5 py-1.5">
        <Select
          @items={{this.itemsForSelect}}
          @selected={{this.selectedKey}}
          @onChange={{this.onSelectionChange}}
          @clearable={{true}}
          @placeholder={{@selectPlaceholder}}
          @size="sm"
          data-test-select-textarea
        />
      </div>

      <textarea
        {{on "input" this.handleTextAreaInput}}
        placeholder={{@placeholder}}
        value={{this.textAreaValue}}
        class="block w-full resize-none border-0 border-gray-200 bg-transparent py-1.5 px-1.5 ring-0 outline-0"
        name={{@name}}
        rows="3"
        id={{this.textareaId}}
        data-test-textarea-input
      />
    </div>
  </template>
}
